// ==================== COMPLETE TRANSFORMATION ENGINE - FIXED ====================

import { INDUSTRY_TRANSFORMATION_DATA } from '../data/transformationData';
import { INDUSTRY_FRAMEWORKS } from '../data/industryFrameworks';
import { FINANCIAL_MODELS, calculateEBITDAImpact } from '../data/financialModels';
import { OPERATING_MODELS } from '../data/operatingModels';
import { ROADMAP_TEMPLATES } from '../data/roadmapTemplates';
import { CHANGE_MANAGEMENT } from '../data/changeManagement';
import { TRANSFORMATION_RISKS, RISK_SCORING } from '../data/riskFrameworks';
import { INDUSTRY_BENCHMARKS, PEER_COMPARISON, BEST_IN_CLASS_TARGETS } from '../data/benchmarkData';
import { TRANSFORMATION_PLAYBOOKS } from '../data/transformationPlaybooks';
import { VALUE_CALCULATORS, calculateTransformationValue } from '../data/valueCalculators';
import { CAPABILITY_MODELS, CAPABILITY_ASSESSMENT_QUESTIONS } from '../data/capabilityModels';
import { TRANSFORMATION_METRICS, SUCCESS_SCORECARD } from '../data/transformationMetrics';
import { MATURITY_DIMENSIONS } from '../data/transformationData';
import { VALUE_ENGINEERING_BENCHMARKS } from '../data/transformationData';
import { TRANSFORMATION_KPIS } from '../data/transformationData';

// ==================== MAIN ENTRY POINT ====================

export const generateCompleteTransformation = (architecture, requirements) => {
  // Ensure requirements has all default values
  const req = {
    industry: 'general',
    problemType: [],
    dataType: [],
    scale: 'smb',
    budget: 'medium',
    expertise: 'intermediate',
    deployment: 'cloud',
    compliance: 'medium',
    timeframe: '3-months',
    priority: 'balanced',
    teamSize: '1-5',
    integrationNeeds: [],
    ...requirements
  };
  
  // Ensure architecture has default values
  const arch = {
    components: [],
    estimatedCost: { total: { high: 500000 } },
    costModel: { total: { high: 500000 } },
    ...architecture
  };
  
  const industryData = INDUSTRY_TRANSFORMATION_DATA[req.industry] || INDUSTRY_TRANSFORMATION_DATA.general;
  const industryFramework = INDUSTRY_FRAMEWORKS[req.industry] || null;
  const industryBenchmark = INDUSTRY_BENCHMARKS[req.industry] || INDUSTRY_BENCHMARKS.general;
  const industryPlaybook = TRANSFORMATION_PLAYBOOKS?.byIndustry?.[req.industry] || null;
  
  // Calculate financial value safely
  const financialValue = calculateTransformationValue(arch, req, industryData);
  
  // Calculate EBITDA impact safely
  const investment = arch.estimatedCost?.total?.high || arch.costModel?.total?.high || 500000;
  const ebitdaImpact = calculateEBITDAImpact(req.scale, investment, req.timeframe);
  
  // Build complete transformation strategy
  return {
    executiveSummary: generateExecutiveSummary(industryData, req, financialValue, ebitdaImpact),
    businessTransformation: generateBusinessTransformation(industryData, req),
    operatingModel: generateOperatingModel(req, OPERATING_MODELS, arch, industryData),
    digitalRoadmap: generateDigitalRoadmap(req, ROADMAP_TEMPLATES),
    maturityAssessment: generateMaturityAssessment(req, arch, MATURITY_DIMENSIONS),
    industryFramework: industryFramework || { frameworkName: 'General AI Framework', keyDimensions: [], regulatoryRequirements: [], useCaseCatalog: [] },
    valueEngineering: generateValueEngineering(req, industryData, financialValue),
    financialImpact: generateFinancialImpact(financialValue, ebitdaImpact, investment),
    operationalExcellence: generateOperationalExcellence(industryData, req),
    changeManagement: generateChangeManagement(CHANGE_MANAGEMENT, req, arch, industryData),
    riskManagement: generateRiskManagement(TRANSACTION_RISKS_SAFE, req),
    kpiFramework: generateKPIFramework(TRANSFORMATION_KPIS, industryData),
    successMetrics: generateSuccessMetrics(TRANSFORMATION_METRICS),
    benchmarks: generateBenchmarks(industryBenchmark, PEER_COMPARISON, BEST_IN_CLASS_TARGETS),
    playbook: industryPlaybook,
    capabilityAssessment: generateCapabilityAssessment(req, CAPABILITY_MODELS),
    successScorecard: SUCCESS_SCORECARD,
    recommendations: generateRecommendations(industryData, req, financialValue, arch),
    quickWins: ROADMAP_TEMPLATES?.quickWins || [],
    valueCalculators: VALUE_CALCULATORS,
    industryData: {
      name: industryData.industryName || 'General',
      valueMultipliers: industryData.valueMultipliers || {},
      maturityBaseline: industryData.digitalMaturityBaseline || 2.0,
      targetMaturity: industryData.targetMaturity || 4.0,
      keyKPIs: industryData.keyKPIs || [],
      priorityUseCases: industryData.priorityUseCases || [],
      transformationTimeline: industryData.transformationTimeline || '12-18 months',
      investmentRange: industryData.investmentRange || { low: 200000, high: 2000000 },
      expectedROI: industryData.expectedROI || '200-300%'
    }
  };
};

// ==================== SAFE RISK DATA ====================

const TRANSACTION_RISKS_SAFE = {
  organizational: TRANSFORMATION_RISKS?.organizational || [
    { risk: 'Resistance to change', impact: 'High', probability: 'Medium', mitigation: 'Change management program', contingency: 'Executive sponsorship', owner: 'CHRO', indicator: 'Low adoption' },
    { risk: 'Skill shortage', impact: 'High', probability: 'Medium', mitigation: 'Training and partnerships', contingency: 'External consultants', owner: 'CTO', indicator: 'Open positions' }
  ],
  technical: TRANSFORMATION_RISKS?.technical || [
    { risk: 'Data quality issues', impact: 'High', probability: 'Medium', mitigation: 'Data governance', contingency: 'Data cleansing', owner: 'CDO', indicator: 'Data quality score' },
    { risk: 'Integration complexity', impact: 'High', probability: 'Medium', mitigation: 'API-first approach', contingency: 'Middleware', owner: 'CIO', indicator: 'Integration failures' }
  ],
  financial: TRANSFORMATION_RISKS?.financial || [
    { risk: 'Budget overrun', impact: 'High', probability: 'Medium', mitigation: 'Phased budgeting', contingency: 'Scope reduction', owner: 'CFO', indicator: 'Spend > 120%' }
  ],
  regulatory: TRANSFORMATION_RISKS?.regulatory || [
    { risk: 'Compliance violation', impact: 'Critical', probability: 'Low', mitigation: 'Compliance framework', contingency: 'Legal counsel', owner: 'CCO', indicator: 'Audit findings' }
  ]
};

// ==================== EXECUTIVE SUMMARY ====================

const generateExecutiveSummary = (industryData, req, financialValue, ebitdaImpact) => {
  return {
    headline: `${industryData.industryName || 'Enterprise'} AI Transformation`,
    vision: `Transform ${industryData.industryName || 'the organization'} through AI-enabled excellence`,
    keyHighlights: [
      `Total Annual Value: $${formatCurrency(financialValue?.totalAnnualValue || 0)}`,
      `Expected ROI: ${Math.round(financialValue?.roi || 0)}%`,
      `Payback Period: ${financialValue?.paybackMonths || 12} months`,
      `EBITDA Improvement: $${formatCurrency(ebitdaImpact?.incrementalEBITDA || 0)}`
    ],
    investmentRequired: `$${formatCurrency(financialValue?.investment || 500000)}`,
    timeline: industryData.transformationTimeline || '12-18 months',
    confidenceLevel: calculateConfidenceLevel(req, financialValue)
  };
};

// ==================== BUSINESS TRANSFORMATION ====================

const generateBusinessTransformation = (industryData, req) => {
  const multipliers = industryData?.valueMultipliers || { revenue: 0.15, cost: 0.20, margin: 0.03, productivity: 0.30 };
  
  return {
    vision: (industryData?.transformationFocus || ['Operational Excellence', 'Customer Centricity', 'Innovation']).join(' and '),
    strategicObjectives: [
      `Achieve ${req.priority === 'cost' ? 'cost leadership' : req.priority === 'quality' ? 'quality excellence' : 'market leadership'} through AI`,
      `Improve operational efficiency by ${Math.round((multipliers.productivity || 0.30) * 100)}%`,
      'Enhance customer satisfaction above 90%',
      `Reduce time-to-market by 40% within ${req.timeframe || '12 months'}`,
      `Achieve ROI of ${industryData?.expectedROI || '200-300%'}`
    ],
    transformationPillars: [
      {
        pillar: 'Customer Centricity',
        description: `Transform customer experience in ${industryData?.industryName || 'the organization'}`,
        impact: 'High',
        timeline: '0-6 months',
        kpi: (industryData?.keyKPIs || ['Customer Satisfaction'])[0]
      },
      {
        pillar: 'Operational Excellence',
        description: 'Streamline operations through intelligent automation',
        impact: 'High',
        timeline: '3-9 months',
        kpi: (industryData?.keyKPIs || ['Operational Efficiency'])[2] || 'Operational Efficiency'
      },
      {
        pillar: 'Data-Driven Culture',
        description: 'Build data literacy and decision-making capabilities',
        impact: 'Medium',
        timeline: '6-12 months',
        kpi: 'Data adoption rate'
      },
      {
        pillar: 'Technology Modernization',
        description: 'Modernize technology stack for AI readiness',
        impact: 'High',
        timeline: '0-12 months',
        kpi: 'Platform adoption'
      }
    ],
    valuePropositions: [
      {
        proposition: 'Revenue Growth',
        description: `AI-driven revenue increase`,
        expectedImpact: `+${Math.round((multipliers.revenue || 0.15) * 100)}%`,
        timeframe: '12 months'
      },
      {
        proposition: 'Cost Reduction',
        description: 'Automation reducing operational costs',
        expectedImpact: `-${Math.round((multipliers.cost || 0.20) * 100)}%`,
        timeframe: '6-12 months'
      },
      {
        proposition: 'Customer Excellence',
        description: 'Enhanced customer experience',
        expectedImpact: '+20% CSAT',
        timeframe: '3-6 months'
      }
    ]
  };
};

// ==================== OPERATING MODEL - FIXED ====================

const generateOperatingModel = (req, models, arch, industryData) => {
  const structure = req.scale === 'enterprise' ? models.governanceStructures.hybrid : 
                    req.scale === 'smb' ? models.governanceStructures.centralized : 
                    models.governanceStructures.centralized;
  
  const componentCount = arch?.components?.length || 0;
  const derivedTeamSize = componentCount > 40 ? '20-50 people' : 
                          componentCount > 20 ? '10-20 people' : 
                          componentCount > 10 ? '5-10 people' : '3-5 people';
  
  const requiredRoles = deriveRequiredRoles(arch);
  const governanceComplexity = req.compliance === 'high' ? 'High' : 'Medium';
  
  const meetingCadence = req.timeframe === '1-month' ? 'Weekly' :
                          req.timeframe === '3-months' ? 'Bi-weekly' : 'Monthly';
  
  return {
    governanceStructure: {
      structureName: structure?.name || 'Centralized CoE',
      description: structure?.description || 'Single AI Center of Excellence',
      rationale: `Selected for ${req.scale} scale with ${componentCount} components`,
      executiveSponsorship: {
        role: req.scale === 'enterprise' ? 'Chief AI Officer' : 'CTO/CIO',
        responsibilities: ['Strategic direction', 'Budget approval', 'Risk oversight'],
        meetingCadence
      },
      steeringCommittee: {
        members: deriveSteeringCommittee(req, arch),
        responsibilities: ['Prioritize use cases', 'Review architecture', 'Track KPIs'],
        meetingCadence
      },
      deliveryTeams: {
        structure: req.scale === 'enterprise' ? 'Multiple squads' : 'Single team',
        teamSize: derivedTeamSize,
        roles: requiredRoles,
        methodology: 'Agile/Scrum'
      }
    },
    decisionRights: customizeDecisionRights(models?.decisionRights || {}, req),
    careerPaths: models?.careerPaths || {},
    teamStructure: req.scale === 'enterprise' ? models?.teamStructures?.matrix : models?.teamStructures?.agile,
    advantages: structure?.advantages || [],
    disadvantages: structure?.disadvantages || [],
    contextSpecific: {
      componentDrivenStructure: `Operating model for ${componentCount} components`,
      skillRequirements: deriveSkillRequirements(arch),
      teamAllocation: deriveTeamAllocation(arch, req),
      budgetAllocation: deriveBudgetAllocation(req)
    }
  };
};

const deriveRequiredRoles = (arch) => {
  const roles = new Set(['Product Owner', 'Data Scientist', 'ML Engineer']);
  const components = arch?.components || [];
  const tags = components.flatMap(c => c.tags || []);
  
  if (tags.some(t => ['gpu', 'training'].includes(t))) roles.add('GPU Specialist');
  if (tags.some(t => ['security', 'encryption'].includes(t))) roles.add('Security Engineer');
  if (tags.some(t => ['monitoring', 'devops'].includes(t))) roles.add('DevOps Engineer');
  if (tags.some(t => ['nlp', 'llm'].includes(t))) roles.add('NLP Engineer');
  if (tags.some(t => ['vision', 'cnn'].includes(t))) roles.add('CV Engineer');
  if (tags.some(t => ['database', 'storage'].includes(t))) roles.add('Data Engineer');
  if (tags.some(t => ['chatbot', 'ui'].includes(t))) roles.add('Frontend Developer');
  roles.add('QA Engineer');
  
  return Array.from(roles);
};

const deriveSteeringCommittee = (req, arch) => {
  const members = ['Business Leader', 'IT Leadership'];
  const tags = (arch?.components || []).flatMap(c => c.tags || []);
  
  if (tags.some(t => ['security', 'compliance'].includes(t))) {
    members.push('CISO');
    members.push('Compliance Officer');
  }
  if (tags.some(t => ['finance', 'cost'].includes(t))) members.push('CFO Representative');
  members.push('Data Science Lead');
  members.push('Operations Head');
  
  return members;
};

const customizeDecisionRights = (decisionRights, req) => {
  const customized = { ...decisionRights };
  
  if (req.compliance === 'high') {
    customized['Compliance Approval'] = {
      approver: 'CCO',
      consultation: 'Legal, Risk',
      informed: 'CEO'
    };
  }
  
  return customized;
};

const deriveSkillRequirements = (arch) => {
  const skills = new Set(['Python', 'SQL', 'ML Fundamentals']);
  const tags = (arch?.components || []).flatMap(c => c.tags || []);
  
  if (tags.some(t => ['gpu', 'cuda'].includes(t))) skills.add('CUDA Programming');
  if (tags.some(t => ['kubernetes', 'docker'].includes(t))) skills.add('Kubernetes');
  if (tags.some(t => ['nlp', 'transformer'].includes(t))) skills.add('Transformers');
  if (tags.some(t => ['vision', 'cnn'].includes(t))) skills.add('Computer Vision');
  if (tags.some(t => ['streaming', 'kafka'].includes(t))) skills.add('Kafka');
  
  return Array.from(skills);
};

const deriveTeamAllocation = (arch, req) => {
  const componentCount = arch?.components?.length || 0;
  const layerDistribution = {};
  
  (arch?.components || []).forEach(comp => {
    const layerName = comp.layerName || 'Other';
    layerDistribution[layerName] = (layerDistribution[layerName] || 0) + 1;
  });
  
  return {
    totalComponents: componentCount,
    layerDistribution,
    suggestedTeamSize: componentCount > 40 ? '15-20' : componentCount > 20 ? '8-12' : '4-6',
    allocationStrategy: req.scale === 'enterprise' ? 'Distributed teams' : 'Single team'
  };
};

const deriveBudgetAllocation = (req) => {
  const budgetMap = {
    'low': { personnel: 0.50, infrastructure: 0.30, training: 0.10, contingency: 0.10 },
    'medium': { personnel: 0.45, infrastructure: 0.35, training: 0.10, contingency: 0.10 },
    'high': { personnel: 0.40, infrastructure: 0.40, training: 0.10, contingency: 0.10 },
    'enterprise': { personnel: 0.35, infrastructure: 0.45, training: 0.10, contingency: 0.10 }
  };
  return budgetMap[req.budget] || budgetMap.medium;
};

// ==================== CHANGE MANAGEMENT - FIXED ====================

const generateChangeManagement = (changeData, req, arch, industryData) => {
  const componentCount = arch?.components?.length || 0;
  const tags = (arch?.components || []).flatMap(c => c.tags || []);
  
  const stakeholders = (changeData?.stakeholderGroups || []).map(group => 
    customizeStakeholder(group, req, arch)
  );
  
  const communicationPlan = (changeData?.communicationPlan || []).map(comm => 
    customizeCommunication(comm, req)
  );
  
  const trainingPrograms = (changeData?.trainingPrograms || []).map(training => 
    customizeTraining(training, arch, tags)
  );
  
  const resistanceManagement = (changeData?.resistanceManagement || []).map(resistance => 
    customizeResistance(resistance, req)
  );
  
  return {
    stakeholderGroups: stakeholders,
    communicationPlan,
    trainingPrograms,
    resistanceManagement,
    contextSpecific: {
      changeComplexity: componentCount > 30 ? 'High' : componentCount > 15 ? 'Medium' : 'Low',
      affectedTeams: deriveAffectedTeams(arch),
      trainingNeeds: deriveTrainingNeeds(tags),
      adoptionStrategy: deriveAdoptionStrategy(req, componentCount),
      changeTimeline: deriveChangeTimeline(req),
      successFactors: deriveSuccessFactors(req, arch)
    }
  };
};

const customizeStakeholder = (group, req, arch) => {
  const componentCount = arch?.components?.length || 0;
  const industryName = req?.industry || 'the organization';
  
  return {
    ...group,
    keyMessages: [
      ...(group?.keyMessages || []),
      `${componentCount} AI components being deployed`,
      `Impact on ${industryName} operations`
    ],
    engagementStrategy: `${group?.engagementStrategy || 'Engagement'} for ${componentCount} component solution`
  };
};

const customizeCommunication = (comm, req) => {
  return {
    ...comm,
    message: `${comm?.message || ''} (${req?.industry || 'general'} specific)`,
    timing: req?.timeframe === '1-month' ? 'Immediate' : (comm?.timing || 'Week 1')
  };
};

const customizeTraining = (training, arch, tags) => {
  const componentCount = arch?.components?.length || 0;
  let topics = [...(training?.topics || [])];
  
  if ((tags || []).includes('nlp') || (tags || []).includes('transformer')) {
    topics.push('NLP and LLM basics');
  }
  if ((tags || []).includes('vision') || (tags || []).includes('cnn')) {
    topics.push('Computer Vision fundamentals');
  }
  if ((tags || []).includes('kubernetes')) {
    topics.push('Container orchestration');
  }
  
  return {
    ...training,
    topics: topics.slice(0, 5),
    description: `${training?.program || 'Training'} for ${componentCount} component architecture`
  };
};

const customizeResistance = (resistance, req) => {
  return {
    ...resistance,
    tactics: req?.scale === 'enterprise' ? 
      [...(resistance?.tactics || []), 'Executive sponsorship program'] : 
      (resistance?.tactics || [])
  };
};

const deriveAffectedTeams = (arch) => {
  const teams = new Set();
  const tags = (arch?.components || []).flatMap(c => c.tags || []);
  
  if (tags.some(t => ['security', 'compliance'].includes(t))) teams.add('Security Team');
  if (tags.some(t => ['database', 'storage'].includes(t))) teams.add('Data Team');
  if (tags.some(t => ['monitoring', 'devops'].includes(t))) teams.add('Operations Team');
  if (tags.some(t => ['chatbot', 'ui'].includes(t))) teams.add('Customer Service Team');
  if (tags.some(t => ['ml', 'model'].includes(t))) teams.add('Data Science Team');
  
  teams.add('Business Users');
  teams.add('IT Support');
  
  return Array.from(teams);
};

const deriveTrainingNeeds = (tags) => {
  const needs = ['AI Fundamentals', 'Data Literacy'];
  
  if ((tags || []).includes('llm') || (tags || []).includes('nlp')) needs.push('Natural Language Processing');
  if ((tags || []).includes('vision') || (tags || []).includes('cnn')) needs.push('Computer Vision');
  if ((tags || []).includes('kubernetes') || (tags || []).includes('docker')) needs.push('Container Orchestration');
  if ((tags || []).includes('monitoring')) needs.push('System Monitoring');
  if ((tags || []).includes('security')) needs.push('AI Security');
  
  return needs.slice(0, 5);
};

const deriveAdoptionStrategy = (req, componentCount) => {
  if (componentCount > 30) return 'Phased adoption by business unit';
  if (componentCount > 15) return 'Departmental rollout with champions';
  return 'Team-by-team adoption';
};

const deriveChangeTimeline = (req) => {
  const timelineMap = {
    '1-month': { preparation: '1 week', training: '1 week', adoption: '2 weeks' },
    '3-months': { preparation: '2 weeks', training: '2 weeks', adoption: '2 months' },
    '6-months': { preparation: '1 month', training: '1 month', adoption: '4 months' },
    '1-year': { preparation: '2 months', training: '2 months', adoption: '8 months' }
  };
  return timelineMap[req?.timeframe || '3-months'] || timelineMap['3-months'];
};

const deriveSuccessFactors = (req, arch) => {
  const factors = ['Executive sponsorship', 'Clear communication', 'Adequate training', 'Quick wins'];
  if (req?.compliance === 'high') factors.push('Compliance adherence');
  if ((arch?.components?.length || 0) > 20) factors.push('Phased implementation');
  return factors;
};

// ==================== DIGITAL ROADMAP ====================

const generateDigitalRoadmap = (req, templates) => {
  return {
    phases: (templates?.phases || []).map(phase => ({
      ...phase,
      budget: calculatePhaseBudget(phase, req)
    })),
    quickWins: (templates?.quickWins || []).slice(0, 5),
    milestones: (templates?.phases || []).map(phase => ({
      phase: phase.phase,
      milestone: phase.deliverables?.[0] || 'Milestone',
      timing: phase.duration
    })),
    totalBudget: (templates?.phases || []).reduce((sum, phase) => sum + (phase.budgetAllocation || 0), 0)
  };
};

const calculatePhaseBudget = (phase, req) => {
  const totalBudget = req?.budget === 'low' ? 100000 : 
                      req?.budget === 'medium' ? 500000 : 
                      req?.budget === 'high' ? 2000000 : 5000000;
  return `$${formatCurrency(totalBudget * (phase?.budgetAllocation || 0.25))}`;
};

// ==================== MATURITY ASSESSMENT ====================

const generateMaturityAssessment = (req, arch, dimensions) => {
  const industryData = INDUSTRY_TRANSFORMATION_DATA[req?.industry || 'general'] || INDUSTRY_TRANSFORMATION_DATA.general;
  
  const assessment = (dimensions || []).map(dim => ({
    dimension: dim.name,
    currentLevel: assessCurrentMaturity(dim.id, req),
    targetLevel: 5,
    gap: 5 - assessCurrentMaturity(dim.id, req),
    levels: dim.levels || [],
    recommendations: getMaturityRecommendations(dim.id)
  }));
  
  return {
    dimensions: assessment,
    overallMaturity: assessment.length > 0 ? 
      Math.round(assessment.reduce((sum, d) => sum + d.currentLevel, 0) / assessment.length * 10) / 10 : 0,
    targetMaturity: 4.5,
    baselineMaturity: industryData?.digitalMaturityBaseline || 2.0,
    maturityGap: 4.5 - (industryData?.digitalMaturityBaseline || 2.0),
    assessmentQuestions: CAPABILITY_ASSESSMENT_QUESTIONS || []
  };
};

const assessCurrentMaturity = (dimension, req) => {
  const scores = {
    'strategy': req?.expertise === 'expert' ? 3 : req?.expertise === 'intermediate' ? 2 : 1,
    'data': req?.dataVolume === 'massive' ? 3 : req?.dataVolume === 'large' ? 2 : 1,
    'talent': req?.expertise === 'expert' ? 4 : req?.expertise === 'intermediate' ? 2 : 1,
    'process': req?.scale === 'enterprise' ? 3 : req?.scale === 'smb' ? 2 : 1,
    'technology': req?.deployment === 'cloud' ? 3 : req?.deployment === 'hybrid' ? 2 : 1
  };
  return scores[dimension] || 2;
};

const getMaturityRecommendations = (dimension) => {
  const recommendations = {
    'strategy': ['Develop AI strategy', 'Appoint AI champion'],
    'data': ['Implement data governance', 'Establish data quality'],
    'talent': ['Hire data scientists', 'Build AI CoE'],
    'process': ['Implement MLOps', 'Create review boards'],
    'technology': ['Deploy AI platform', 'Implement monitoring']
  };
  return recommendations[dimension] || [];
};

// ==================== VALUE ENGINEERING ====================

const generateValueEngineering = (req, industryData, financialValue) => {
  const multipliers = industryData?.valueMultipliers || { revenue: 0.15, cost: 0.20, margin: 0.03, asset: 0.20, productivity: 0.30, throughput: 0.25, workingCapital: 0.15 };
  
  return {
    ebitdaImpact: {
      revenueUplift: {
        description: 'AI-driven revenue increase',
        expectedRange: `+${Math.round((multipliers.revenue || 0.15) * 100)}%`,
        annualValue: financialValue?.revenueUplift || 0,
        drivers: ['New products', 'Price optimization']
      },
      costReduction: {
        description: 'Operational cost savings',
        expectedRange: `-${Math.round((multipliers.cost || 0.20) * 100)}%`,
        annualValue: financialValue?.costReduction || 0,
        drivers: ['Automation', 'Process optimization']
      },
      marginImprovement: {
        description: 'EBITDA margin expansion',
        expectedRange: `+${Math.round((multipliers.margin || 0.03) * 100)} points`,
        annualValue: financialValue?.incrementalEBITDA || 0,
        drivers: ['Revenue growth', 'Cost reduction']
      }
    },
    assetUtilization: {
      description: 'Optimized asset deployment',
      expectedImprovement: `+${Math.round((multipliers.asset || 0.20) * 100)}%`,
      annualValue: financialValue?.assetValue || 0
    },
    engineeringProductivity: {
      description: 'Automated workflows',
      expectedImprovement: `+${Math.round((multipliers.productivity || 0.30) * 100)}%`,
      annualValue: financialValue?.productivityValue || 0
    },
    manufacturingThroughput: {
      description: 'Optimized production flow',
      expectedImprovement: `+${Math.round((multipliers.throughput || 0.25) * 100)}%`,
      annualValue: financialValue?.productivityValue || 0
    },
    workingCapital: {
      description: 'Optimized working capital',
      expectedImprovement: `-${Math.round((multipliers.workingCapital || 0.15) * 100)}%`,
      annualValue: financialValue?.workingCapitalRelease || 0
    }
  };
};

// ==================== FINANCIAL IMPACT ====================

const generateFinancialImpact = (financialValue, ebitdaImpact, investment) => {
  return {
    totalAnnualValue: financialValue?.totalAnnualValue || 0,
    totalInvestment: investment || 500000,
    roi: financialValue?.roi || 0,
    paybackPeriod: financialValue?.paybackMonths || 12,
    npv: financialValue?.npv || 0,
    irr: calculateIRR(investment || 500000, financialValue?.totalAnnualValue || 0, 3),
    valueBreakdown: {
      revenue: financialValue?.revenueUplift || 0,
      costSavings: financialValue?.costReduction || 0,
      assetOptimization: financialValue?.assetValue || 0,
      productivity: financialValue?.productivityValue || 0,
      workingCapital: financialValue?.workingCapitalRelease || 0
    },
    ebitdaDetails: {
      incrementalEBITDA: ebitdaImpact?.incrementalEBITDA || 0,
      newEBITDA: ebitdaImpact?.newEBITDA || 0,
      newMargin: ebitdaImpact?.newEBITDAMargin || 0
    }
  };
};

// ==================== OPERATIONAL EXCELLENCE ====================

const generateOperationalExcellence = (industryData, req) => {
  return {
    processOptimization: [
      {
        process: 'Decision Making',
        improvement: '50% faster decisions',
        value: 'High'
      },
      {
        process: 'Data Processing',
        improvement: '90% latency reduction',
        value: 'High'
      },
      {
        process: 'Quality Control',
        improvement: '95% error reduction',
        value: 'High'
      }
    ],
    automationOpportunities: [
      { area: 'Back-office operations', automationPotential: '70-80%', complexity: 'Medium' },
      { area: 'Customer service', automationPotential: '50-60%', complexity: 'Low' },
      { area: 'Data entry', automationPotential: '90%', complexity: 'Low' }
    ]
  };
};

// ==================== RISK MANAGEMENT ====================

const generateRiskManagement = (riskData, req) => {
  const allRisks = [
    ...(riskData?.organizational || []),
    ...(riskData?.technical || []),
    ...(riskData?.financial || []),
    ...(riskData?.regulatory || [])
  ];
  
  const scoredRisks = allRisks.map(risk => {
    const score = RISK_SCORING?.riskScore?.(risk.impact, risk.probability) || 6;
    return {
      ...risk,
      riskScore: score,
      riskLevel: RISK_SCORING?.riskLevel?.(score) || 'Medium'
    };
  });
  
  return {
    risks: scoredRisks,
    totalRisks: scoredRisks.length,
    criticalRisks: scoredRisks.filter(r => r.riskLevel === 'Critical').length,
    highRisks: scoredRisks.filter(r => r.riskLevel === 'High').length,
    mediumRisks: scoredRisks.filter(r => r.riskLevel === 'Medium').length,
    lowRisks: scoredRisks.filter(r => r.riskLevel === 'Low').length
  };
};

// ==================== KPI FRAMEWORK ====================

const generateKPIFramework = (kpiData, industryData) => {
  return {
    financial: kpiData?.financial || [],
    operational: kpiData?.operational || [],
    customer: kpiData?.customer || [],
    innovation: kpiData?.innovation || [],
    industrySpecific: (industryData?.keyKPIs || []).map(kpi => ({
      kpi,
      baseline: 'Industry average',
      target: 'Best-in-class',
      frequency: 'Monthly'
    }))
  };
};

// ==================== SUCCESS METRICS ====================

const generateSuccessMetrics = (metrics) => {
  return {
    leading: metrics?.leading || {},
    lagging: metrics?.lagging || {},
    valueRealization: metrics?.valueRealization || {}
  };
};

// ==================== BENCHMARKS ====================

const generateBenchmarks = (industryBenchmark, peerComparison, bestInClass) => {
  return {
    industryBenchmarks: industryBenchmark || {},
    peerComparison: peerComparison || {},
    bestInClassTargets: bestInClass || {}
  };
};

// ==================== CAPABILITY ASSESSMENT ====================

const generateCapabilityAssessment = (req, models) => {
  return {
    capabilities: models?.aiCapabilities || {},
    transformationCapabilities: models?.transformationCapabilities || {},
    assessmentQuestions: CAPABILITY_ASSESSMENT_QUESTIONS || []
  };
};

// ==================== RECOMMENDATIONS ====================

const generateRecommendations = (industryData, req, financialValue, arch) => {
  const recommendations = [];
  
  if (req?.priority === 'cost') {
    recommendations.push({
      category: 'Cost Optimization',
      recommendation: 'Focus on automation use cases',
      impact: 'High',
      effort: 'Medium',
      timeline: '3-6 months'
    });
  }
  
  recommendations.push({
    category: 'Quick Wins',
    recommendation: `Start with ${(industryData?.priorityUseCases || ['Process Automation'])[0]}`,
    impact: 'High',
    effort: 'Low',
    timeline: '1-3 months'
  });
  
  return recommendations;
};

// ==================== HELPER FUNCTIONS ====================

const formatCurrency = (value) => {
  if (!value || value === 0) return '0';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return `${Math.round(value)}`;
};

const calculateConfidenceLevel = (req, financialValue) => {
  let confidence = 70;
  if (req?.expertise === 'expert') confidence += 15;
  if (req?.expertise === 'beginner') confidence -= 15;
  if ((financialValue?.roi || 0) > 300) confidence += 10;
  if ((financialValue?.roi || 0) < 100) confidence -= 10;
  return Math.min(Math.max(confidence, 0), 100);
};

const calculateIRR = (investment, annualReturn, years) => {
  if (!investment || !annualReturn) return '0.0%';
  let rate = 0.1;
  for (let i = 0; i < 50; i++) {
    let npv = -investment;
    let derivative = 0;
    for (let j = 1; j <= years; j++) {
      npv += annualReturn / Math.pow(1 + rate, j);
      derivative -= j * annualReturn / Math.pow(1 + rate, j + 1);
    }
    if (derivative === 0) break;
    const newRate = rate - npv / derivative;
    if (Math.abs(newRate - rate) < 0.0001) return `${(newRate * 100).toFixed(1)}%`;
    rate = newRate;
  }
  return `${(rate * 100).toFixed(1)}%`;
};